import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { LoginRequest, SignupRequest, AuthResponse, User } from '../types/auth';
import { API_CONFIG, API_ENDPOINTS } from '../config/api';

const REFRESH_STORAGE_KEY = 'auth_refresh_token';

type RequestWithRefresh = InternalAxiosRequestConfig & { _refreshAttempted?: boolean };

class AuthService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.FULL_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (r) => r,
      async (error) => {
        const originalRequest = error.config as RequestWithRefresh | undefined;
        if (
          !error.response ||
          error.response.status !== 401 ||
          !originalRequest ||
          originalRequest._refreshAttempted
        ) {
          return Promise.reject(error);
        }

        const url = String(originalRequest.url || '');
        if (
          url.includes('/auth/login') ||
          url.includes('/auth/signup') ||
          url.includes('/auth/signup-with-invite') ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/apple')
        ) {
          return Promise.reject(error);
        }

        originalRequest._refreshAttempted = true;
        const rt = localStorage.getItem(REFRESH_STORAGE_KEY);
        if (!rt) {
          return Promise.reject(error);
        }

        try {
          const { data } = await axios.post<AuthResponse>(
            `${API_CONFIG.FULL_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
            { refresh_token: rt },
            { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
          );
          localStorage.setItem('auth_token', data.access_token);
          localStorage.setItem(REFRESH_STORAGE_KEY, data.refresh_token);
          if (data.user) {
            localStorage.setItem('auth_user', JSON.stringify(data.user));
          }
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return this.client(originalRequest);
        } catch {
          localStorage.removeItem('auth_token');
          localStorage.removeItem(REFRESH_STORAGE_KEY);
          localStorage.removeItem('auth_user');
          return Promise.reject(error);
        }
      }
    );
  }

  /** Exchange refresh token for new credentials (e.g. cold start when access JWT expired). */
  async tryRefresh(): Promise<boolean> {
    const rt = localStorage.getItem(REFRESH_STORAGE_KEY);
    if (!rt) return false;
    try {
      const { data } = await axios.post<AuthResponse>(
        `${API_CONFIG.FULL_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
        { refresh_token: rt },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
      );
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem(REFRESH_STORAGE_KEY, data.refresh_token);
      if (data.user) {
        localStorage.setItem('auth_user', JSON.stringify(data.user));
      }
      return true;
    } catch {
      return false;
    }
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
    return response.data;
  }

  async signInWithApple(): Promise<AuthResponse> {
    const clientId = import.meta.env.VITE_APPLE_WEB_CLIENT_ID as string | undefined;
    const redirectURI = import.meta.env.VITE_APPLE_REDIRECT_URI as string | undefined;

    if (!clientId || !redirectURI) {
      throw new Error('Apple Sign-In is not configured for this environment.');
    }

    AppleID.auth.init({
      clientId,
      scope: 'name email',
      redirectURI,
      usePopup: true,
    });

    const result = await AppleID.auth.signIn();

    const fullName = result.user?.name
      ? [result.user.name.firstName, result.user.name.lastName].filter(Boolean).join(' ')
      : undefined;

    const response = await this.client.post<AuthResponse>(API_ENDPOINTS.AUTH.APPLE, {
      identity_token: result.authorization.id_token,
      authorization_code: result.authorization.code,
      ...(fullName ? { full_name: fullName } : {}),
    });
    return response.data;
  }

  async signup(credentials: SignupRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>(API_ENDPOINTS.AUTH.SIGNUP, credentials);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>(API_ENDPOINTS.AUTH.ME);
    return response.data;
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await this.client.put<User>(API_ENDPOINTS.AUTH.PROFILE, userData);
    return response.data;
  }

  async changePassword(passwords: { currentPassword: string; newPassword: string }): Promise<void> {
    await this.client.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, passwords);
  }

  /** Best-effort server-side refresh token revocation. */
  async notifyLogout(): Promise<void> {
    const rt = localStorage.getItem(REFRESH_STORAGE_KEY);
    const token = localStorage.getItem('auth_token');
    try {
      await axios.post(
        `${API_CONFIG.FULL_BASE_URL}/auth/logout`,
        rt ? { refresh_token: rt } : {},
        {
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
    } catch {
      /* ignore */
    }
  }
}

export const authService = new AuthService();
